/**
 * Mobile-specific bottom sheet drawer for context linking
 * Provides touch-optimized navigation between sessions and patients lists
 */

import { type LinkedPatientContext, type LinkedSessionContext } from '@/atoms/chatAtoms';
import { IS_CHAT_ATTACHMENTS_ENABLED } from '@/features/flags/legacy-flags';
import { Button } from '@heidi/design-system/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@heidi/design-system/drawer';
import { FileUploadButton } from '@heidi/design-system/file-upload';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { Dispatch } from '@/store';
import {
  AudioLines,
  ChevronLeft,
  ChevronRight,
  Paperclip,
  Plus,
  UserRound,
} from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { contextLinkingButtonMessages } from './ContextLinkingButton.messages';
import { MobilePatientsList } from './ContextLinkingPatientsList';
import { MobileSessionsList } from './ContextLinkingSessionsList';
import { track } from '@/lib/analytics/track';

interface ContextLinkingMobileDrawerProps {
  disabled?: boolean;
  isContextLocked?: boolean;
  isInSessionEvidenceShown?: boolean;
  onFilesAdded?: (files: File[]) => void;
  linkedSession: LinkedSessionContext | null;
  linkedPatient: LinkedPatientContext | null;
  onSelectSession: (session: LinkedSessionContext) => void;
  onSelectPatient: (patient: LinkedPatientContext) => void;
  onClearSession: () => void;
  onClearPatient: () => void;
}

type MobileView = 'main' | 'sessions' | 'patients';

/** Reusable sub-view with back button header pattern */
interface MobileDrawerSubViewProps {
  title: string;
  onBack: () => void;
  children: ReactNode;
}

const MobileDrawerSubView = ({ title, onBack, children }: MobileDrawerSubViewProps) => (
  <>
    <DrawerHeader className="border-b border-border pb-3">
      <div className="flex items-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="absolute start-2"
          iconOnly
          disableAutomaticSvgSizing
        >
          <ChevronLeft size={20} className="rtl:-scale-x-100" />
        </Button>
        <DrawerTitle className="flex-1 text-center">{title}</DrawerTitle>
      </div>
    </DrawerHeader>
    <div className="max-h-[60vh] overflow-y-auto p-2">{children}</div>
  </>
);

export const ContextLinkingMobileDrawer = ({
  disabled = false,
  isContextLocked = false,
  isInSessionEvidenceShown = false,
  onFilesAdded,
  linkedSession,
  linkedPatient,
  onSelectSession,
  onSelectPatient,
  onClearSession,
  onClearPatient,
}: ContextLinkingMobileDrawerProps) => {
  const intl = useIntl();
  const dispatch = useDispatch<Dispatch>();
  const [isOpen, setIsOpen] = useState(false);
  const [mobileView, setMobileView] = useState<MobileView>('main');

  // Track button clicks
  const trackAddSessionClicked = useCallback(() => {
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ChatContextAddSessionClicked,
      props: {
        is_mobile: true,
      },
    });
  }, [dispatch]);

  const trackAttachPatientClicked = useCallback(() => {
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ChatContextAttachPatientClicked,
      props: {
        is_mobile: true,
      },
    });
  }, [dispatch]);

  const trackAddFileClicked = useCallback(() => {
    track({ options: { amplitude: false },
      event_name: TrackingEventName.ChatContextAddFileClicked,
      props: {
        is_mobile: true,
      },
    });
  }, [dispatch]);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setMobileView('main');
    }
  }, []);

  const handleSelectSession = useCallback(
    (session: LinkedSessionContext) => {
      onSelectSession(session);
      setIsOpen(false);
      setMobileView('main');
    },
    [onSelectSession]
  );

  const handleSelectPatient = useCallback(
    (patient: LinkedPatientContext) => {
      onSelectPatient(patient);
      setIsOpen(false);
      setMobileView('main');
    },
    [onSelectPatient]
  );

  const handleBackToMain = useCallback(() => setMobileView('main'), []);

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="lg"
          disabled={disabled}
          data-testid="context-linking-button"
          iconOnly
          disableAutomaticSvgSizing
        >
          <Plus size={16} />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[85vh]">
        {/* Main menu view */}
        {mobileView === 'main' && (
          <>
            <DrawerHeader className="border-b border-border pb-3">
              <DrawerTitle className="text-center">
                <FormattedMessage {...contextLinkingButtonMessages.addContext} />
              </DrawerTitle>
            </DrawerHeader>
            <div className="flex flex-col p-2">
              {isInSessionEvidenceShown && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      trackAddSessionClicked();
                      setMobileView('sessions');
                    }}
                    className="flex w-full items-center justify-between rounded-lg p-3 text-start hover:bg-surface-hover active:bg-surface-pressed"
                  >
                    <div className="flex items-center gap-3">
                      <AudioLines size={20} className="stroke-[1.5] text-text-secondary" />
                      <span className="text-sm font-medium text-text-primary">
                        <FormattedMessage
                          {...contextLinkingButtonMessages.linkAConsultation}
                        />
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-text-tertiary rtl:-scale-x-100" />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      trackAttachPatientClicked();
                      setMobileView('patients');
                    }}
                    className="flex w-full items-center justify-between rounded-lg p-3 text-start hover:bg-surface-hover active:bg-surface-pressed"
                  >
                    <div className="flex items-center gap-3">
                      <UserRound size={20} className="stroke-[1.5] text-text-secondary" />
                      <span className="text-sm font-medium text-text-primary">
                        <FormattedMessage {...contextLinkingButtonMessages.linkAPatient} />
                      </span>
                    </div>
                    <ChevronRight size={16} className="text-text-tertiary rtl:-scale-x-100" />
                  </button>
                </>
              )}

              {IS_CHAT_ATTACHMENTS_ENABLED() && onFilesAdded && (
                <FileUploadButton
                  asChild
                  multiple
                  onFilesAccepted={(files) => {
                    trackAddFileClicked();
                    onFilesAdded(files);
                    setIsOpen(false);
                  }}
                  className="w-full"
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg p-3 text-start hover:bg-surface-hover active:bg-surface-pressed"
                  >
                    <Paperclip size={20} className="stroke-[1.5] text-text-secondary" />
                    <span className="text-sm font-medium text-text-primary">
                      <FormattedMessage {...contextLinkingButtonMessages.addFile} />
                    </span>
                  </button>
                </FileUploadButton>
              )}
            </div>
          </>
        )}

        {/* Sessions list view */}
        {mobileView === 'sessions' && (
          <MobileDrawerSubView
            title={intl.formatMessage(contextLinkingButtonMessages.linkAConsultation)}
            onBack={handleBackToMain}
          >
            <MobileSessionsList
              linkedSession={linkedSession}
              onSelect={handleSelectSession}
              onClear={onClearSession}
              disabled={isContextLocked}
            />
          </MobileDrawerSubView>
        )}

        {/* Patients list view */}
        {mobileView === 'patients' && (
          <MobileDrawerSubView
            title={intl.formatMessage(contextLinkingButtonMessages.linkAPatient)}
            onBack={handleBackToMain}
          >
            <MobilePatientsList
              linkedPatient={linkedPatient}
              onSelect={handleSelectPatient}
              onClear={onClearPatient}
              disabled={isContextLocked}
            />
          </MobileDrawerSubView>
        )}
      </DrawerContent>
    </Drawer>
  );
};
